import React, { useState } from "react";
import {
  Card,
  Tabs,
  Input,
  Button,
  Descriptions,
  Table,
  Tag,
  Space,
  Typography,
  Select,
  DatePicker,
  Tooltip,
  message,
  Alert,
} from "antd";
import {
  SearchOutlined,
  RollbackOutlined,
  WarningOutlined,
  EditOutlined,
  UndoOutlined,
  ReloadOutlined,
  ToolOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PermissionRender from "../components/PermissionRender";
import { getPaymentMethodInFrench } from "../utils/payment";
import {
  getSubscriptionById,
  searchSubscriptions,
} from "../api/subscriptions/subscriptionsRequest";
import {
  getInfraction,
  searchInfractionByFilter,
} from "../api/infractions/infractionRequest";
import ReverseCancelSubscriptionModal from "../components/ReverseCancelSubscriptionModal";
import ModifyPaidEcheanceModal from "../components/ModifyPaidEcheanceModal";
import RollbackEcheancePaymentModal from "../components/RollbackEcheancePaymentModal";
import RollbackMacaronPaymentModal from "../components/RollbackMacaronPaymentModal";
import ModifyPaidFeeModal from "../components/ModifyPaidFeeModal";
import ChangeEcheanceAmountModal from "../components/ChangeEcheanceAmountModal";
import ChangeRegistrationFeeModal from "../components/ChangeRegistrationFeeModal";
import EditPaymentModal from "../components/EditPaymentModal";
import RollbackInfractionPaymentModal from "../components/RollbackInfractionPaymentModal";
import ReverseCancelInfractionModal from "../components/ReverseCancelInfractionModal";
import RetroactiveWarningModal from "../components/RetroactiveWarningModal";
import AuditHistoryDrawer from "../components/AuditHistoryDrawer";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AUDIT_PERMISSION = ["data_correction", "audit_history"];

const unwrap = (resp) => (resp && resp.data !== undefined ? resp.data : resp);
const asList = (resp) => {
  const d = resp && resp.data !== undefined ? resp.data : resp;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data; // paginator
  return [];
};

const fmt = (v) =>
  v === null || v === undefined || isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)} DH`;

const formatDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const statusTag = (status) => {
  const map = {
    active: { color: "green", label: "Actif" },
    canceled: { color: "red", label: "Annulé" },
    pending: { color: "orange", label: "En attente" },
    paid: { color: "green", label: "Payé" },
    overdue: { color: "red", label: "En retard" },
    rejected: { color: "red", label: "Rejeté" },
    blocked: { color: "volcano", label: "Bloqué" },
    finalized: { color: "blue", label: "Finalisé" },
  };
  const m = map[status] || { color: "default", label: status || "—" };
  return <Tag color={m.color}>{m.label}</Tag>;
};

// Client name: companies use `name`; individuals use first_name + last_name.
const clientName = (client) => {
  if (!client) return "—";
  if (client.name && String(client.name).trim()) return client.name;
  const full = [client.first_name, client.last_name].filter(Boolean).join(" ").trim();
  return full || "—";
};

// Payment source: human-readable French label.
const sourceLabel = (s) =>
  ({
    agent: "Agent",
    citizen_mobile: "Citoyen (mobile)",
    citizen_web: "Citoyen (web)",
    wallet: "Portefeuille",
    cmi: "CMI",
    fatourati: "Fatourati",
    parkpass: "ParkPass",
  }[s] || s || "—");

const detailTypeLabel = (t) =>
  ({
    echeance: "Échéance",
    registration_fee: "Frais d'inscription",
    reactivation_fee: "Frais de réactivation",
    macaron_fee: "Frais de macaron",
  }[t] || t);

// Shared pagination config that always resets cleanly and shows the total.
const tablePagination = (page, setPage) => ({
  current: page,
  pageSize: 8,
  onChange: setPage,
  showSizeChanger: false,
  showTotal: (total) => `${total} résultat(s)`,
});

// Resolve the payment that settled a paid echeance, with the agent balance, the full
// payment breakdown and the payment total (so we can show mixed fees and un-pay it).
const resolveEcheancePayment = (subscription, echeanceId) => {
  const payments = subscription?.payments || [];
  for (const p of payments) {
    const details = p.details || [];
    if (details.some((d) => d.type === "echeance" && String(d.echeance_id) === String(echeanceId))) {
      const bal = p.user?.balance;
      return {
        found: true,
        source: p.source ?? null,
        agentBalance: bal !== undefined && bal !== null ? Number(bal) : null,
        breakdown: details.map((d) => ({ type: d.type, amount: d.amount })),
        paymentAmount: p.amount !== undefined && p.amount !== null ? Number(p.amount) : null,
      };
    }
  }
  return { found: false, source: null, agentBalance: null, breakdown: null, paymentAmount: null };
};

// Derive a human label + macaron flag for a payment from its details.
const describePayment = (payment) => {
  const types = (payment?.details || []).map((d) => d.type);
  const isMacaron = types.includes("macaron_fee") && !types.includes("echeance");
  const labels = [...new Set(types.map((t) => detailTypeLabel(t)))];
  return { isMacaron, label: labels.join(" + ") || "—" };
};

// ─── Subscription / Echeance correction tab ───
const SubscriptionTab = () => {
  const [filters, setFilters] = useState({ search: "", matricule: "", status: undefined, id: "" });
  const [results, setResults] = useState(null);
  const [page, setPage] = useState(1);
  const [searching, setSearching] = useState(false);

  const [subscription, setSubscription] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [reverseModal, setReverseModal] = useState(false);
  const [echeanceModal, setEcheanceModal] = useState({ open: false, echeance: null, agentBalance: null, breakdown: null });
  const [unpayModal, setUnpayModal] = useState({ open: false, echeance: null, agentBalance: null, breakdown: null, paymentAmount: null });
  const [editEcheanceModal, setEditEcheanceModal] = useState({ open: false, echeance: null });
  const [macaronModal, setMacaronModal] = useState({ open: false, payment: null });
  const [feeModal, setFeeModal] = useState({ open: false, detail: null, agentBalance: null });
  const [editPaymentModal, setEditPaymentModal] = useState({ open: false, payment: null });
  const [regFeeModal, setRegFeeModal] = useState(false);
  const [audit, setAudit] = useState({ open: false });

  const loadDetail = async (id) => {
    if (!id) return;
    setLoadingDetail(true);
    try {
      const data = unwrap(await getSubscriptionById(id));
      setSubscription(data);
      if (!data) message.info("Abonnement introuvable");
    } catch (error) {
      message.error(error.response?.data?.message || "Abonnement introuvable");
      setSubscription(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const runSearch = async () => {
    if (filters.id) {
      setResults(null);
      loadDetail(filters.id);
      return;
    }
    if (!filters.search && !filters.matricule && !filters.status) {
      message.info("Renseignez au moins un critère de recherche");
      return;
    }
    setSearching(true);
    try {
      const list = asList(
        await searchSubscriptions({
          search: filters.search || undefined,
          matricule: filters.matricule || undefined,
          status: filters.status || undefined,
          per_page: 100,
        })
      );
      setPage(1);
      setResults(list);
      if (list.length === 0) message.info("Aucun abonnement trouvé");
    } catch (error) {
      message.error(error.response?.data?.message || "Erreur lors de la recherche");
    } finally {
      setSearching(false);
    }
  };

  const reset = () => {
    setFilters({ search: "", matricule: "", status: undefined, id: "" });
    setResults(null);
    setPage(1);
    setSubscription(null);
  };

  const refresh = () => loadDetail(subscription?.id);

  const resultColumns = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "Contrat", dataIndex: "contract_number", render: (v) => v || "—" },
    { title: "Client", render: (_, r) => clientName(r.client) },
    { title: "Véhicule", render: (_, r) => r.vehicle?.matricule || "—" },
    { title: "Statut", dataIndex: "status", render: statusTag },
    {
      title: "",
      width: 120,
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => loadDetail(r.id)}>
          Sélectionner
        </Button>
      ),
    },
  ];

  const echeances = [...(subscription?.echeances || [])].sort(
    (a, b) => new Date(a.start_date) - new Date(b.start_date)
  );
  const payments = subscription?.payments || [];

  const echeanceColumns = [
    { title: "ID", dataIndex: "id", width: 60 },
    { title: "Période", render: (_, r) => `${formatDate(r.start_date)} → ${formatDate(r.end_date)}` },
    { title: "Montant", dataIndex: "amount", render: fmt, sorter: (a, b) => a.amount - b.amount },
    { title: "Statut", dataIndex: "status", render: statusTag },
    {
      title: "Action",
      width: 230,
      render: (_, r) => {
        if (r.status !== "paid") {
          return (
            <PermissionRender requiredPermission={["subscriptions", "update"]}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => setEditEcheanceModal({ open: true, echeance: r })}
              >
                Modifier montant
              </Button>
            </PermissionRender>
          );
        }

        // Paid echeance: correction/un-pay only allowed when the settling payment is agent-sourced.
        const pay = resolveEcheancePayment(subscription, r.id);
        const disabled = !pay.found || pay.source !== "agent";
        const reason = !pay.found
          ? "Paiement introuvable pour cette échéance"
          : pay.source !== "agent"
          ? `Paiement non-agent (source : ${pay.source}) — correction impossible`
          : "";

        return (
          <Space size={4}>
            <PermissionRender requiredPermission={["subscriptions", "modify_paid_echeance"]}>
              <Tooltip title={reason}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={disabled}
                  onClick={() =>
                    setEcheanceModal({
                      open: true,
                      echeance: r,
                      agentBalance: pay.agentBalance,
                      breakdown: pay.breakdown,
                    })
                  }
                >
                  Corriger
                </Button>
              </Tooltip>
            </PermissionRender>
            <PermissionRender requiredPermission={["subscriptions", "rollback_echeance_payment"]}>
              <Tooltip title={reason}>
                <Button
                  size="small"
                  danger
                  icon={<RollbackOutlined />}
                  disabled={disabled}
                  onClick={() =>
                    setUnpayModal({
                      open: true,
                      echeance: r,
                      agentBalance: pay.agentBalance,
                      breakdown: pay.breakdown,
                      paymentAmount: pay.paymentAmount,
                    })
                  }
                >
                  Annuler paiement
                </Button>
              </Tooltip>
            </PermissionRender>
          </Space>
        );
      },
    },
  ];

  const paymentColumns = [
    { title: "Référence", dataIndex: "reference", render: (v) => v || "—" },
    { title: "Type", render: (_, r) => describePayment(r).label },
    { title: "Montant", dataIndex: "amount", render: fmt },
    { title: "Méthode", dataIndex: "payment_method", render: (v) => (v ? getPaymentMethodInFrench(v) : "—") },
    { title: "Source", dataIndex: "source", render: (v) => (v ? <Tag>{sourceLabel(v)}</Tag> : "—") },
    { title: "Date", dataIndex: "payment_date", render: formatDate },
    {
      title: "Action",
      width: 200,
      render: (_, r) => {
        const { isMacaron } = describePayment(r);
        return (
          <Space size={4}>
            {r.source === "agent" && (
              <PermissionRender requiredPermission={["payment", "update"]}>
                <Button size="small" icon={<EditOutlined />} onClick={() => setEditPaymentModal({ open: true, payment: r })}>
                  Détails
                </Button>
              </PermissionRender>
            )}
            {isMacaron && r.source === "agent" && (
              <PermissionRender requiredPermission={["subscriptions", "rollback_macaron"]}>
                <Button size="small" danger icon={<RollbackOutlined />} onClick={() => setMacaronModal({ open: true, payment: r })}>
                  Annuler
                </Button>
              </PermissionRender>
            )}
          </Space>
        );
      },
    },
  ];

  const paymentDetailColumns = (payment) => [
    { title: "Type", dataIndex: "type", render: detailTypeLabel },
    { title: "Montant", dataIndex: "amount", render: fmt },
    {
      title: "Action",
      width: 200,
      render: (_, d) => {
        if (["registration_fee", "reactivation_fee"].includes(d.type) && payment.source === "agent") {
          return (
            <PermissionRender requiredPermission={["subscriptions", "modify_paid_fee"]}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  setFeeModal({
                    open: true,
                    detail: d,
                    agentBalance: payment.user?.balance !== undefined && payment.user?.balance !== null ? Number(payment.user.balance) : null,
                  })
                }
              >
                Corriger
              </Button>
            </PermissionRender>
          );
        }
        if (d.type === "echeance") return <Text type="secondary">Corriger via le tableau Échéances</Text>;
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  const regFeeDisabledReason =
    subscription?.registration_fee_paid === true
      ? "Frais déjà payés — corrigez via le détail du paiement"
      : subscription?.status !== "active"
      ? "Disponible uniquement pour un abonnement actif"
      : "";

  return (
    <>
      <Space wrap style={{ width: "100%", marginBottom: 4 }}>
        <Input style={{ width: 220 }} placeholder="Client, CIN ou contrat" value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} onPressEnter={runSearch} allowClear />
        <Input style={{ width: 150 }} placeholder="Matricule" value={filters.matricule}
          onChange={(e) => setFilters((f) => ({ ...f, matricule: e.target.value }))} onPressEnter={runSearch} allowClear />
        <Select style={{ width: 150 }} placeholder="Statut" allowClear value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={[
            { value: "active", label: "Actif" },
            { value: "canceled", label: "Annulé" },
            { value: "pending", label: "En attente" },
            { value: "rejected", label: "Rejeté" },
          ]} />
        <Input style={{ width: 120 }} placeholder="ID direct" value={filters.id}
          onChange={(e) => setFilters((f) => ({ ...f, id: e.target.value }))} onPressEnter={runSearch} allowClear />
        <Button type="primary" icon={<SearchOutlined />} loading={searching} onClick={runSearch}>Rechercher</Button>
        <Button icon={<ReloadOutlined />} onClick={reset}>Réinitialiser</Button>
      </Space>

      {results && (
        <Table rowKey="id" size="small" style={{ marginTop: 12 }} columns={resultColumns} dataSource={results}
          loading={searching} pagination={tablePagination(page, setPage)} locale={{ emptyText: "Aucun abonnement" }} />
      )}

      {subscription && (
        <Card style={{ marginTop: 16 }} size="small" loading={loadingDetail}>
          <Descriptions
            title={`Abonnement #${subscription.id}${subscription.contract_number ? ` · ${subscription.contract_number}` : ""}`}
            column={2}
            size="small"
            bordered
            extra={
              <Space>
                <PermissionRender requiredPermission={AUDIT_PERMISSION}>
                  <Button icon={<HistoryOutlined />} onClick={() => setAudit({ open: true })}>Historique</Button>
                </PermissionRender>
                <PermissionRender requiredPermission={["subscriptions", "update"]}>
                  <Tooltip title={regFeeDisabledReason}>
                    <Button icon={<EditOutlined />} disabled={!!regFeeDisabledReason} onClick={() => setRegFeeModal(true)}>
                      Frais d'inscription
                    </Button>
                  </Tooltip>
                </PermissionRender>
                <PermissionRender requiredPermission={["subscriptions", "reverse_cancel"]}>
                  <Tooltip title={subscription.status !== "canceled" ? "Disponible uniquement pour un abonnement annulé" : ""}>
                    <Button danger icon={<UndoOutlined />} disabled={subscription.status !== "canceled"} onClick={() => setReverseModal(true)}>
                      Inverser l'annulation
                    </Button>
                  </Tooltip>
                </PermissionRender>
              </Space>
            }
          >
            <Descriptions.Item label="Statut">{statusTag(subscription.status)}</Descriptions.Item>
            <Descriptions.Item label="Client">{clientName(subscription.client)}</Descriptions.Item>
            <Descriptions.Item label="Véhicule">{subscription.vehicle?.matricule || "—"}</Descriptions.Item>
            <Descriptions.Item label="Frais d'inscription">
              {fmt(subscription.registration_fee)} {subscription.registration_fee_paid ? <Tag color="green">payés</Tag> : <Tag>non payés</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Début">{formatDate(subscription.start_date)}</Descriptions.Item>
            <Descriptions.Item label="Fin">{formatDate(subscription.end_date)}</Descriptions.Item>
          </Descriptions>

          <Title level={5} style={{ marginTop: 16 }}>Échéances ({echeances.length})</Title>
          <Table rowKey="id" size="small" columns={echeanceColumns} dataSource={echeances}
            pagination={false} scroll={{ y: 320 }} locale={{ emptyText: "Aucune échéance" }} />

          <Title level={5} style={{ marginTop: 16 }}>Paiements ({payments.length})</Title>
          <Table
            rowKey="id"
            size="small"
            columns={paymentColumns}
            dataSource={payments}
            pagination={false}
            scroll={{ y: 320 }}
            locale={{ emptyText: "Aucun paiement" }}
            expandable={{
              rowExpandable: (p) => (p.details || []).length > 0,
              expandedRowRender: (p) => (
                <Table rowKey="id" size="small" pagination={false} dataSource={p.details || []} columns={paymentDetailColumns(p)} />
              ),
            }}
          />
        </Card>
      )}

      <ReverseCancelSubscriptionModal visible={reverseModal} subscription={subscription}
        onCancel={() => setReverseModal(false)} onSuccess={() => { setReverseModal(false); refresh(); }} />

      <ModifyPaidEcheanceModal visible={echeanceModal.open} echeance={echeanceModal.echeance}
        agentBalance={echeanceModal.agentBalance} breakdown={echeanceModal.breakdown}
        onCancel={() => setEcheanceModal({ open: false, echeance: null, agentBalance: null, breakdown: null })}
        onSuccess={() => { setEcheanceModal({ open: false, echeance: null, agentBalance: null, breakdown: null }); refresh(); }} />

      <RollbackEcheancePaymentModal visible={unpayModal.open} echeance={unpayModal.echeance}
        agentBalance={unpayModal.agentBalance} breakdown={unpayModal.breakdown} paymentAmount={unpayModal.paymentAmount}
        onCancel={() => setUnpayModal({ open: false, echeance: null, agentBalance: null, breakdown: null, paymentAmount: null })}
        onSuccess={() => { setUnpayModal({ open: false, echeance: null, agentBalance: null, breakdown: null, paymentAmount: null }); refresh(); }} />

      <ChangeEcheanceAmountModal visible={editEcheanceModal.open} echeance={editEcheanceModal.echeance}
        onCancel={() => setEditEcheanceModal({ open: false, echeance: null })}
        onSuccess={() => { setEditEcheanceModal({ open: false, echeance: null }); refresh(); }} />

      <ModifyPaidFeeModal visible={feeModal.open} detail={feeModal.detail} agentBalance={feeModal.agentBalance}
        onCancel={() => setFeeModal({ open: false, detail: null, agentBalance: null })}
        onSuccess={() => { setFeeModal({ open: false, detail: null, agentBalance: null }); refresh(); }} />

      <EditPaymentModal visible={editPaymentModal.open} payment={editPaymentModal.payment}
        onCancel={() => setEditPaymentModal({ open: false, payment: null })}
        onSuccess={() => { setEditPaymentModal({ open: false, payment: null }); refresh(); }} />

      <ChangeRegistrationFeeModal visible={regFeeModal} subscription={subscription}
        onCancel={() => setRegFeeModal(false)} onSuccess={() => { setRegFeeModal(false); refresh(); }} />

      <RollbackMacaronPaymentModal visible={macaronModal.open} payment={macaronModal.payment}
        onCancel={() => setMacaronModal({ open: false, payment: null })}
        onSuccess={() => { setMacaronModal({ open: false, payment: null }); refresh(); }} />

      <AuditHistoryDrawer open={audit.open} onClose={() => setAudit({ open: false })}
        entity="subscription" entityId={subscription?.id} title={`Historique — Abonnement #${subscription?.id ?? ""}`} />
    </>
  );
};

// ─── Infraction correction tab ───
const InfractionTab = () => {
  const [filters, setFilters] = useState({
    reference: "",
    matricule: "",
    status: undefined,
    id_agent: "",
    range: [dayjs().subtract(3, "day"), dayjs()],
    id: "",
  });
  const [results, setResults] = useState(null);
  const [page, setPage] = useState(1);
  const [searching, setSearching] = useState(false);

  const [infraction, setInfraction] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [rollbackModal, setRollbackModal] = useState(false);
  const [warningModal, setWarningModal] = useState(false);
  const [uncancelModal, setUncancelModal] = useState(false);
  const [audit, setAudit] = useState({ open: false });

  const loadDetail = async (id) => {
    if (!id) return;
    setLoadingDetail(true);
    try {
      const data = unwrap(await getInfraction(id));
      setInfraction(data);
      if (!data) message.info("Infraction introuvable");
    } catch (error) {
      message.error(error.response?.data?.error || "Infraction introuvable");
      setInfraction(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const runSearch = async () => {
    if (filters.id) {
      setResults(null);
      loadDetail(filters.id);
      return;
    }
    setSearching(true);
    try {
      const [start, end] = filters.range || [];
      const list = asList(
        await searchInfractionByFilter({
          reference: filters.reference || undefined,
          matricule: filters.matricule || undefined,
          status: filters.status || undefined,
          id_agent: filters.id_agent || undefined,
          start_date: start ? start.format("YYYY-MM-DD") : undefined,
          end_date: end ? end.format("YYYY-MM-DD") : undefined,
        })
      );
      setPage(1);
      setResults(list);
      if (list.length === 0) message.info("Aucune infraction trouvée");
    } catch (error) {
      message.error(error.response?.data?.error || "Erreur lors de la recherche");
    } finally {
      setSearching(false);
    }
  };

  const reset = () => {
    setFilters({
      reference: "",
      matricule: "",
      status: undefined,
      id_agent: "",
      range: [dayjs().subtract(3, "day"), dayjs()],
      id: "",
    });
    setResults(null);
    setPage(1);
    setInfraction(null);
  };

  const refresh = () => loadDetail(infraction?.id);
  const agent = infraction?.payment?.user || null;

  const resultColumns = [
    { title: "Référence", dataIndex: "reference", render: (v) => v || "—" },
    { title: "Matricule", dataIndex: "matricule", render: (v) => v || "—" },
    { title: "Montant", dataIndex: "amount", render: fmt },
    { title: "Statut", dataIndex: "status", render: statusTag },
    {
      title: "Avertissement",
      dataIndex: "warning_id",
      width: 130,
      render: (v) => (v ? <Tag color="blue">Oui #{v}</Tag> : <Tag>Non</Tag>),
    },
    { title: "Date", dataIndex: "datetime_infraction", render: formatDate },
    {
      title: "",
      width: 120,
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => loadDetail(r.id)}>
          Sélectionner
        </Button>
      ),
    },
  ];

  return (
    <>
      <Space wrap style={{ width: "100%", marginBottom: 4 }}>
        <Input style={{ width: 160 }} placeholder="Référence" value={filters.reference}
          onChange={(e) => setFilters((f) => ({ ...f, reference: e.target.value }))} onPressEnter={runSearch} allowClear />
        <Input style={{ width: 150 }} placeholder="Matricule" value={filters.matricule}
          onChange={(e) => setFilters((f) => ({ ...f, matricule: e.target.value }))} onPressEnter={runSearch} allowClear />
        <Select style={{ width: 140 }} placeholder="Statut" allowClear value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={[
            { value: "paid", label: "Payé" },
            { value: "pending", label: "En attente" },
            { value: "canceled", label: "Annulé" },
            { value: "blocked", label: "Bloqué" },
          ]} />
        <RangePicker value={filters.range} onChange={(v) => setFilters((f) => ({ ...f, range: v }))} format="DD/MM/YYYY" allowClear={false} />
        <Input style={{ width: 110 }} placeholder="ID agent" value={filters.id_agent}
          onChange={(e) => setFilters((f) => ({ ...f, id_agent: e.target.value }))} onPressEnter={runSearch} allowClear />
        <Input style={{ width: 110 }} placeholder="ID direct" value={filters.id}
          onChange={(e) => setFilters((f) => ({ ...f, id: e.target.value }))} onPressEnter={runSearch} allowClear />
        <Button type="primary" icon={<SearchOutlined />} loading={searching} onClick={runSearch}>Rechercher</Button>
        <Button icon={<ReloadOutlined />} onClick={reset}>Réinitialiser</Button>
      </Space>

      {results && (
        <Table rowKey="id" size="small" style={{ marginTop: 12 }} columns={resultColumns} dataSource={results}
          loading={searching} pagination={tablePagination(page, setPage)} locale={{ emptyText: "Aucune infraction" }} />
      )}

      {infraction && (
        <Card style={{ marginTop: 16 }} size="small" loading={loadingDetail}>
          <Descriptions
            title={`Infraction ${infraction.reference || `#${infraction.id}`}`}
            column={2}
            size="small"
            bordered
            extra={
              <Space wrap>
                <PermissionRender requiredPermission={AUDIT_PERMISSION}>
                  <Button icon={<HistoryOutlined />} onClick={() => setAudit({ open: true })}>Historique</Button>
                </PermissionRender>
                <PermissionRender requiredPermission={["infractions", "reverse_cancel"]}>
                  <Tooltip title={infraction.status !== "canceled" ? "Disponible uniquement pour une infraction annulée" : ""}>
                    <Button icon={<UndoOutlined />} disabled={infraction.status !== "canceled"} onClick={() => setUncancelModal(true)}>
                      Inverser l'annulation
                    </Button>
                  </Tooltip>
                </PermissionRender>
                <PermissionRender requiredPermission={["infractions", "rollback_payment"]}>
                  <Tooltip title={infraction.status !== "paid" ? "Disponible uniquement pour une infraction payée" : ""}>
                    <Button danger icon={<RollbackOutlined />} disabled={infraction.status !== "paid"} onClick={() => setRollbackModal(true)}>
                      Annuler le paiement
                    </Button>
                  </Tooltip>
                </PermissionRender>
                <PermissionRender requiredPermission={["warnings", "create_retroactive"]}>
                  <Tooltip title={infraction.warning_id ? "Un avertissement existe déjà" : ""}>
                    <Button icon={<WarningOutlined />} disabled={!!infraction.warning_id} onClick={() => setWarningModal(true)}>
                      Avertissement rétroactif
                    </Button>
                  </Tooltip>
                </PermissionRender>
              </Space>
            }
          >
            <Descriptions.Item label="Statut">{statusTag(infraction.status)}</Descriptions.Item>
            <Descriptions.Item label="Matricule">{infraction.matricule || "—"}</Descriptions.Item>
            <Descriptions.Item label="Montant">{fmt(infraction.amount)}</Descriptions.Item>
            <Descriptions.Item label="Date">{formatDate(infraction.datetime_infraction)}</Descriptions.Item>
            <Descriptions.Item label="Avertissement lié">
              {infraction.warning_id ? <Tag color="blue">#{infraction.warning_id}</Tag> : <Tag>Aucun</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Source paiement">
              {infraction.payment?.source ? <Tag>{sourceLabel(infraction.payment.source)}</Tag> : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Agent collecteur">{agent?.full_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Solde agent">
              {agent ? <Text strong>{fmt(agent.balance)}</Text> : <Text type="secondary">—</Text>}
            </Descriptions.Item>
          </Descriptions>

          {infraction.payment && infraction.payment.source !== "agent" && (
            <Alert style={{ marginTop: 8 }} type="warning" showIcon
              message="Ce paiement n'a pas été effectué par un agent ; l'annulation sera refusée (isolation par source)." />
          )}
        </Card>
      )}

      <RollbackInfractionPaymentModal visible={rollbackModal} infraction={infraction}
        onCancel={() => setRollbackModal(false)} onSuccess={() => { setRollbackModal(false); refresh(); }} />

      <ReverseCancelInfractionModal visible={uncancelModal} infraction={infraction}
        onCancel={() => setUncancelModal(false)} onSuccess={() => { setUncancelModal(false); refresh(); }} />

      <RetroactiveWarningModal visible={warningModal} infraction={infraction}
        onCancel={() => setWarningModal(false)} onSuccess={() => { setWarningModal(false); refresh(); }} />

      <AuditHistoryDrawer open={audit.open} onClose={() => setAudit({ open: false })}
        entity="infraction" entityId={infraction?.id} title={`Historique — Infraction #${infraction?.id ?? ""}`} />
    </>
  );
};

const DataCorrectionPage = () => {
  return (
    <div style={{ padding: 24 }}>
      <Space align="center" style={{ marginBottom: 8 }}>
        <ToolOutlined style={{ fontSize: 22, color: "#6e00ff" }} />
        <Title level={3} style={{ margin: 0 }}>
          Correction & Surcharge de données
        </Title>
      </Space>
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message="Panneau d'actions critiques"
        description="Ces actions contournent la logique métier standard. Chaque opération exige un motif. À utiliser avec précaution."
      />
      <Card>
        <Tabs
          items={[
            { key: "subscription", label: "Abonnement / Échéance", children: <SubscriptionTab /> },
            { key: "infraction", label: "Infraction", children: <InfractionTab /> },
          ]}
        />
      </Card>
    </div>
  );
};

export default DataCorrectionPage;
